import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import Logo from "@/components/Logo";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const { lang, t } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success(t("auth.loginSuccess"));
      navigate("/app");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      let msg;
      if (status === 401) msg = t("auth.loginErr401");
      else if (status === 403) msg = detail || t("auth.loginErr403default");
      else if (status >= 500) msg = t("auth.loginErr500");
      else if (!err?.response) msg = t("auth.loginErrNetwork");
      else msg = detail || t("auth.loginErrDefault");
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const homeHref = lang === "en" ? "/" : "/tr";
  const emailPh = lang === "en" ? "you@yourbusiness.com" : "ornek@isletmem.com";

  return (
    <div className="min-h-screen flex items-center justify-center px-6 relative">
      <div className="absolute inset-0 bg-grid opacity-20" />
      <div className="absolute top-6 left-6"><Link to={homeHref}><Logo /></Link></div>
      <div className="w-full max-w-md relative z-10">
        <div className="glass-strong rounded-3xl p-8">
          <h1 className="font-display text-3xl font-bold text-white mb-2">{t("auth.welcomeBack")}</h1>
          <p className="text-slate-400 text-sm mb-8">{t("auth.loginSubtitle")}</p>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="text-xs text-slate-400 uppercase tracking-wider">{t("auth.email")}</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all"
                placeholder={emailPh}
                data-testid="login-email"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 uppercase tracking-wider">{t("auth.password")}</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all"
                placeholder="••••••••"
                data-testid="login-password"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="neon-btn w-full text-white font-semibold rounded-xl py-3.5 flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="login-submit"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              {t("auth.loginBtn")}
            </button>
          </form>

          <div className="mt-4 text-center text-sm">
            <Link to="/sifremi-unuttum" className="text-slate-400 hover:text-cyan-300" data-testid="goto-forgot-password">
              {t("auth.forgotPassword")}
            </Link>
          </div>

          <div className="mt-3 text-center text-sm text-slate-400">
            {t("auth.noAccount")}{" "}
            <Link to="/kayit" className="text-cyan-400 hover:text-cyan-300" data-testid="goto-register">
              {t("auth.createFree")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
